import { logger } from '@tuic/logger';
import { Mps } from '../../index';
import { errorContains } from '../../utils';
import { GetRecipientResponse, TypeEnum } from '../../generated/mps';
import { Locale } from '@tuic/i18n';

type GetNewsletterRecipientQuery = {
  type: TypeEnum | 'NL_PREFERENCE';
  mailId: string;
};

/**
 * Fetches an existing newsletter recipient.
 */
export async function getNewsletterRecipient(
  query: GetNewsletterRecipientQuery,
): Promise<GetRecipientResponse | null> {
  let { data, error } = await Mps.getRecipient({
    query,
  });

  if (error) {
    logger.error('[MPS]: Failed fetching newsletter recipient.');
    logger.error(error);

    return null;
  }

  return data ?? null;
}

export enum ADD_NEWSLETTER_RECIPIENT_RESPONSE {
  SUCCESS,
  GENERAL_ERROR,
  RECIPIENT_ALREADY_EXISTS,
}

type AddNewsletterRecipientBody = Omit<Mps.AddRecipient, 'locale'> & {
  locale?: Mps.AddRecipient['locale'] | Locale;
};

/**
 * Adds a new newsletter recipient.
 */
export const addNewsletterRecipient = async (
  body: AddNewsletterRecipientBody,
): Promise<ADD_NEWSLETTER_RECIPIENT_RESPONSE> => {
  let { error } = await Mps.addRecipient({
    body: {
      ...body,
      locale: body.locale
        ? (body.locale.toUpperCase() as Mps.AddRecipient['locale'])
        : undefined,
    },
  });

  if (!error) {
    return ADD_NEWSLETTER_RECIPIENT_RESPONSE.SUCCESS;
  }

  if (errorContains(error, 'already exists')) {
    return ADD_NEWSLETTER_RECIPIENT_RESPONSE.RECIPIENT_ALREADY_EXISTS;
  }

  logger.error('[MPS]: Failed adding recipient.');
  logger.error(error);

  return ADD_NEWSLETTER_RECIPIENT_RESPONSE.GENERAL_ERROR;
};

export enum UPDATE_NEWSLETTER_RECIPIENT_RESPONSE {
  SUCCESS,
  GENERAL_ERROR,
  RECIPIENT_DOES_NOT_EXIST,
}

type UpdateRecipientBody = Omit<Mps.UpdateRecipientRequest, 'locale'> & {
  locale?: Mps.UpdateRecipientRequest['locale'] | Locale;
};

/**
 * Updates an existing newsletter recipient.
 */
export async function updateNewsletterRecipient(
  body: UpdateRecipientBody,
): Promise<UPDATE_NEWSLETTER_RECIPIENT_RESPONSE> {
  let { error } = await Mps.updateRecipient({
    body: {
      ...body,
      locale: body.locale
        ? (body.locale.toUpperCase() as Mps.UpdateRecipientRequest['locale'])
        : undefined,
    },
  });

  if (!error) {
    return UPDATE_NEWSLETTER_RECIPIENT_RESPONSE.SUCCESS;
  }

  if (errorContains(error, 'recipient not found')) {
    return UPDATE_NEWSLETTER_RECIPIENT_RESPONSE.RECIPIENT_DOES_NOT_EXIST;
  }

  logger.error(
    `[MPS] Failed updating recipient. Recipient: ${JSON.stringify(body)}`,
  );
  logger.error(error);

  return UPDATE_NEWSLETTER_RECIPIENT_RESPONSE.GENERAL_ERROR;
}
